import styled from '@emotion/styled'
import React from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '../../../api/graphql'
import Routes from '../../../startup/routes'
import Loading from '../misc/Loading'
import { getCalendars } from './billingGating'
import BusinessCalendarLanding from './BusinessCalendarLanding'
import {
  BUSINESS_SUBSCRIPTION_QUERY,
  BusinessSubscriptionQueryResponse,
  humanizeSubscriptionStatus,
} from './BusinessCalendarQueries'
import { Hugeicon } from '../../../icons/Hugeicon'

// Re-exported for callers/tests that imported it from this component.
export { getCalendars }

const BusinessCalendarList: React.FC = () => {
  const { data, loading, error } = useQuery<BusinessSubscriptionQueryResponse>(BUSINESS_SUBSCRIPTION_QUERY)

  if (loading) {
    return <Loading className="isCentered isDark" />
  }

  if (error) {
    // eslint-disable-next-line no-console
    console.error('BusinessCalendarList query error:', error)
    return (
      <Container>
        <Header>
          <Title>Error loading calendars</Title>
          <Subtitle>Please try refreshing the page</Subtitle>
        </Header>
      </Container>
    )
  }

  const calendars = getCalendars(data)

  if (calendars.length === 0) {
    return <BusinessCalendarLanding />
  }

  return (
    <Container>
      <Header>
        <Title>Your Business Calendars</Title>
        <Subtitle>Manage your calendars and track performance</Subtitle>
      </Header>

      <CalendarGrid>
        {calendars.map(org => (
          <CalendarCard key={org.id} to={Routes.businessCalendarDetail(Routes.idFromGlobalId(org.id))}>
            <CardHeader>
              <OrgIcon>
                <CalendarIcon />
              </OrgIcon>
              <StatusBadge status={org.businessSubscription?.status || 'inactive'}>
                {org.businessSubscription?.trialActive
                  ? `Trial (${org.businessSubscription.trialDaysRemaining}d)`
                  : org.businessSubscription
                    ? humanizeSubscriptionStatus(org.businessSubscription.status)
                    : 'Inactive'}
              </StatusBadge>
            </CardHeader>

            <CardContent>
              <OrgName>{org.name}</OrgName>
              <FeedTitle>{org.businessSubscription?.contentFeed?.title || 'Events Calendar'}</FeedTitle>
            </CardContent>

            <CardFooter>
              <ViewLink>
                Manage Calendar
                <ArrowIcon />
              </ViewLink>
            </CardFooter>
          </CalendarCard>
        ))}

        <AddCalendarCard to={Routes.businessCalendarSignup()}>
          <AddIcon>
            <PlusIcon />
          </AddIcon>
          <AddText>Add New Calendar</AddText>
          <AddSubtext>Set up a calendar for another organization</AddSubtext>
        </AddCalendarCard>
      </CalendarGrid>
    </Container>
  )
}

export default BusinessCalendarList

// Icons
const CalendarIcon = () => (
  <Hugeicon name="calendar" size={24} />
)

const ArrowIcon = () => (
  <Hugeicon name="arrow-right-long" size={16} />
)

const PlusIcon = () => (
  <Hugeicon name="plus" size={24} />
)

// Styled Components
const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 32px 24px;
`

const Header = styled.div`
  margin-bottom: 32px;
`

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #102638;
  margin: 0 0 4px;
`

const Subtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
`

const CalendarCard = styled(Link)`
  display: flex;
  flex-direction: column;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    border-color: #3b82f6;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
    transform: translateY(-2px);
  }
`

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
`

const OrgIcon = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #1e3a5f 0%, #2d4a6f 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`

const StatusBadge = styled.span<{ status: string }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
  background: ${({ status }) => {
    switch (status) {
      case 'active':
        return '#dcfce7'
      case 'trialing':
        return '#dbeafe'
      case 'past_due':
        return '#fef3c7'
      default:
        return '#f3f4f6'
    }
  }};
  color: ${({ status }) => {
    switch (status) {
      case 'active':
        return '#166534'
      case 'trialing':
        return '#1e40af'
      case 'past_due':
        return '#92400e'
      default:
        return '#374151'
    }
  }};
`

const CardContent = styled.div`
  flex: 1;
  margin-bottom: 16px;
`

const OrgName = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #102638;
  margin: 0 0 4px;
`

const FeedTitle = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0;
`

const CardFooter = styled.div`
  padding-top: 16px;
  border-top: 1px solid #f3f4f6;
`

const ViewLink = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #3b82f6;
  transition: gap 0.2s ease;
`

const AddCalendarCard = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 32px 20px;
  text-decoration: none;
  text-align: center;
  transition: all 0.2s ease;
  min-height: 200px;

  &:hover {
    border-color: #9ca3af;
    background: #f3f4f6;
  }
`

const AddIcon = styled.div`
  width: 48px;
  height: 48px;
  background: #e5e7eb;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  margin-bottom: 12px;
`

const AddText = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
`

const AddSubtext = styled.span`
  font-size: 13px;
  color: #6b7280;
`
